import React, { useState, useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, LineElement, CategoryScale, LinearScale, Title } from 'chart.js';
import { filterReviewsByPlatform } from '../../utility/reviewByPlatforms';
import './countGraph.css';

ChartJS.register(LineElement, CategoryScale, LinearScale, Title);

const platformColors = {
  GYG: '#F1866A',
  Viator: '#2A6B6D',
};

const CountGraph = ({ reviewData = [], title = "Total Reviews Count", chartId = "countGraphChart" }) => {
  const months = [
    "Jan'", "Feb'", "Mar'", "Apr'", "May'", "Jun'",
    "Jul'", "Aug'", "Sept'", "Oct'", "Nov'", "Dec'"
  ];

  const { gygReviews, viatorReviews } = filterReviewsByPlatform(reviewData);

  const extractYear = (review) => {
    const dateString = review[3];
    if (dateString && typeof dateString === "string" && dateString.includes("/")) {
      const [, , year] = dateString.split("/").map(Number);
      if (year >= 1900 && year <= 2099) return year;
    }
    return null;
  };

  const availableYears = useMemo(() => {
    const yearsSet = new Set();
    reviewData.forEach(review => {
      const year = extractYear(review);
      if (year) yearsSet.add(year);
    });
    return Array.from(yearsSet).sort((a, b) => a - b);
  }, [reviewData]);

  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);

  const countReviewsByMonth = (reviews) => {
    const counts = {};
    reviews.forEach((review, index) => {
      try {
        const dateString = review[3];
        if (!dateString || typeof dateString !== "string" || !dateString.includes("/")) return;
        const [day, month, year] = dateString.split("/").map(Number);
        if (month < 1 || month > 12 || year !== selectedYear) return;
        const monthYear = `${months[month - 1]} ${year % 100}`;
        counts[monthYear] = (counts[monthYear] || 0) + 1;
      } catch (error) {
        console.error(`Error processing review at index ${index}:`, review, error);
      }
    });
    return counts;
  };

  const gygCounts = countReviewsByMonth(gygReviews);
  const viatorCounts = countReviewsByMonth(viatorReviews);

  const labels = months.map((m) => `${m} ${selectedYear % 100}`);

  const gygData = labels.map(month => gygCounts.hasOwnProperty(month) ? gygCounts[month] : null);
  const viatorData = labels.map(month => viatorCounts.hasOwnProperty(month) ? viatorCounts[month] : null);


  const data = {
    labels,
    datasets: [
      {
        label: 'GYG Reviews',
        data: gygData,
        fill: false,
        borderColor: '#F1866A',
        borderWidth: 2,
        tension: 0.3,
      },
      {
        label: 'Viator Reviews',
        data: viatorData,
        fill: false,
        borderColor: '#2A6B6D',
        borderWidth: 2,
        tension: 0.3,
      },
    ],
  };

  const options = {
    responsive: true,
    plugins: {
      title: {
        display: true,
        text: title,
      },
      legend: {
        display: false,
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Month',
        },
        grid: {
          color: 'rgba(211, 211, 211, 0.35)',
        },
      },
      y: {
        title: {
          display: true,
          text: 'Total Reviews Count',
        },
        ticks: {
          stepSize: 100,
        },
        grid: {
          color: 'rgba(211, 211, 211, 0.35)',
        },
      },
    },
  };

  const renderLegend = () => (
    <div style={{ marginTop: '0px' }}>
      <ul style={{ listStyle: 'none', padding: 0 }}>
        {Object.keys(platformColors).map((platform) => (
          <li key={platform} style={{ display: 'flex', alignItems: 'center', marginBottom: '5px' }}>
            <div style={{
              width: '10px',
              height: '10px',
              backgroundColor: platformColors[platform],
              borderRadius: '50%',
              marginRight: '10px',
            }}></div>
            <span>{platform}</span>
          </li>
        ))}
      </ul>
    </div>
  );

  const renderYearDropdown = () => (
    <div className='graphDropdown'>
      <label htmlFor="yearSelect">Year: </label>
      <select id="yearSelect" value={selectedYear} onChange={(e) => setSelectedYear(Number(e.target.value))}>
        {availableYears.map((year) => (
          <option key={year} value={year}>
            {year}
          </option>
        ))}
      </select>
    </div>
  );

  return (
    <div className='graphComponent'>
      <div className='graphActual'>
        <div className='graphHeading'>
          <div className='countHeading'>{title}</div>
          {renderYearDropdown()}
        </div>
        <Line id={chartId} data={data} options={options} />
        <div className='legendContainer'>{renderLegend()}</div>
      </div>
    </div>
  );
};

export default CountGraph;
